type SessionCacheEntry<T> = {
    value: T;
    expiresAt: number;
};

const canUseSessionStorage = (): boolean => {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
};

export const getSessionCache = <T>(key: string): T | null => {
    if (!canUseSessionStorage()) {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) {
            return null;
        }

        const entry = JSON.parse(raw) as SessionCacheEntry<T>;
        if (!entry || typeof entry.expiresAt !== 'number') {
            window.sessionStorage.removeItem(key);
            return null;
        }

        if (Date.now() > entry.expiresAt) {
            window.sessionStorage.removeItem(key);
            return null;
        }

        return entry.value ?? null;
    } catch {
        window.sessionStorage.removeItem(key);
        return null;
    }
};

export const setSessionCache = <T>(key: string, value: T, ttlMs: number): void => {
    if (!canUseSessionStorage()) {
        return;
    }

    const safeTtlMs = Number.isFinite(ttlMs) && ttlMs > 0 ? ttlMs : 0;
    const entry: SessionCacheEntry<T> = {
        value,
        expiresAt: Date.now() + safeTtlMs
    };

    try {
        window.sessionStorage.setItem(key, JSON.stringify(entry));
    } catch {
        // ignore storage quota errors
    }
};

export const removeSessionCache = (key: string): void => {
    if (!canUseSessionStorage()) {
        return;
    }

    window.sessionStorage.removeItem(key);
};

export const removeSessionCacheByPrefix = (prefix: string): void => {
    if (!canUseSessionStorage()) {
        return;
    }

    const keysToDelete: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (key && key.startsWith(prefix)) {
            keysToDelete.push(key);
        }
    }

    keysToDelete.forEach((key) => window.sessionStorage.removeItem(key));
};
